import { useState, useEffect } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Lock, ArrowLeft, CheckCircle, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useTranslation } from "react-i18next";

export default function ResetPassword() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const search = useSearch();
  const token = new URLSearchParams(search).get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenError(t("authPages.noResetToken"));
        setValidating(false);
        return;
      }

      try {
        const response = await fetch(`/api/auth/validate-reset-token?token=${encodeURIComponent(token)}`);
        const data = await response.json();
        
        if (data.valid) {
          setTokenValid(true);
        } else {
          setTokenError(data.message || t("authPages.invalidOrExpired"));
        }
      } catch (error) {
        setTokenError(t("authPages.unableToValidate"));
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast({
        title: t("authPages.passwordsDontMatchTitle"),
        description: t("authPages.passwordsDontMatchDesc"),
        variant: "destructive"
      });
      return;
    }

    if (password.length < 8) {
      toast({
        title: t("authPages.passwordTooShortTitle"),
        description: t("authPages.passwordTooShortDesc"),
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    try {
      await apiRequest("POST", "/api/auth/reset-password", { 
        token, 
        newPassword: password 
      });
      setSuccess(true);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "An error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">{t("authPages.validatingResetLink")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background p-4">
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-30 -z-10" />

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="w-full max-w-md space-y-8"
      >
        <div className="space-y-6">
          <div className="h-16 w-16 bg-primary rounded-2xl flex items-center justify-center shadow-xl shadow-primary/20 rotate-3">
            <Building2 className="h-8 w-8 text-primary-foreground" />
          </div>
          <div className="space-y-2">
            <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground">
              {success ? t("authPages.resetSuccessTitle") : tokenValid ? t("authPages.createNewPassword") : t("authPages.invalidLink")}
            </h1>
            <p className="text-muted-foreground">
              {success 
                ? t("authPages.resetSuccessSubtitle") 
                : tokenValid 
                  ? t("authPages.enterNewPassword")
                  : tokenError
              }
            </p>
          </div>
        </div>

        {success ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-green-50 border border-green-200 rounded-xl p-6 space-y-4"
          >
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <h2 className="text-lg font-semibold text-green-900">{t("authPages.successHeading")}</h2>
            </div>
            <p className="text-green-800">
              {t("authPages.successBody")}
            </p>
            <Link href="/login">
              <Button className="w-full mt-4" data-testid="button-go-to-login">
                {t("authPages.goToLogin")}
              </Button>
            </Link>
          </motion.div>
        ) : tokenValid ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider ps-1">
                  {t("authPages.newPasswordLabel")}
                </Label>
                <div className="relative group">
                  <Lock className="absolute start-3 top-3 h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  <Input 
                    type="password" 
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 ps-10 bg-background border-muted-foreground/20 hover:border-primary/50 focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all rounded-lg"
                    required
                    minLength={8}
                    data-testid="input-new-password"
                  />
                </div>
                <p className="text-xs text-muted-foreground ps-1">
                  {t("authPages.mustBe8Chars")}
                </p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider ps-1">
                  {t("authPages.confirmPasswordLabel")}
                </Label>
                <div className="relative group">
                  <Lock className="absolute start-3 top-3 h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  <Input 
                    type="password" 
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="h-12 ps-10 bg-background border-muted-foreground/20 hover:border-primary/50 focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all rounded-lg"
                    required
                    data-testid="input-confirm-password"
                  />
                </div>
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full h-12 text-base font-medium bg-gradient-to-r from-primary to-blue-600 hover:from-blue-600 hover:to-primary text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300 rounded-lg"
              data-testid="button-reset-password"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t("authPages.resetting")}
                </span>
              ) : (
                t("authPages.resetPasswordBtn")
              )}
            </Button>
          </form>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-red-50 border border-red-200 rounded-xl p-6 space-y-4"
          >
            <div className="flex items-center gap-3">
              <AlertCircle className="h-8 w-8 text-red-600" />
              <h2 className="text-lg font-semibold text-red-900">{t("authPages.linkInvalidHeading")}</h2>
            </div>
            <p className="text-red-800">
              {tokenError}
            </p>
            <Link href="/forgot-password">
              <Button variant="outline" className="w-full mt-4" data-testid="button-request-new-link">
                {t("authPages.requestNewLink")}
              </Button>
            </Link>
          </motion.div>
        )}

        <div className="text-center">
          <Link href="/login" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" />
            {t("authPages.backToLogin")}
          </Link>
        </div>

        <div className="pt-6 flex items-center justify-between border-t border-border/40">
          <p className="text-xs text-muted-foreground font-medium">
            {t("authPages.securityLabel")}
          </p>
          <div className="flex gap-4">
            <div className="h-2 w-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]" />
            <span className="text-xs text-muted-foreground font-mono">{t("authPages.systemOnlineCaps")}</span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
